/**
 * 🩹 PATCH-351 (2026-02) · Major Events Pill
 * ------------------------------------------
 * Small gold pill that lives inside the site Header, visible only
 * when at least one published, non-past event exists. Clicking it
 * routes to /events. Kept intentionally generic ("Major Events")
 * because multiple events can be live simultaneously — per SA
 * directive: "don't name the event due to multiple".
 *
 * Data source · GET /api/public/events/summary
 *   returns { count, next_event_at }
 *   Silently hides on network failure so a public visitor never sees
 *   a broken chip.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/site`;

function fmtNext(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return null; }
}

export default function MajorEventsPill({ compact = false }) {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/public/events/summary`)
      .then((r) => { if (live) setSummary(r.data); })
      .catch(() => { if (live) setSummary(null); });
    return () => { live = false; };
  }, []);

  if (!summary || (summary.count || 0) === 0) return null;

  const next = fmtNext(summary.next_event_at);

  return (
    <Link
      to="/events"
      data-testid="major-events-pill"
      data-event-count={summary.count}
      className={
        compact
          ? "inline-flex items-center gap-1.5 h-8 px-3 bg-[#D4AF37] hover:bg-[#F4D88A] text-[#0A0612] font-mono text-[10px] tracking-[0.18em] uppercase rounded-full transition-colors shadow-sm"
          : "inline-flex items-center gap-1.5 h-8 px-3 bg-gradient-to-r from-[#D4AF37] to-[#F4D88A] hover:from-[#F4D88A] hover:to-[#D4AF37] text-[#0A0612] font-mono text-[10px] tracking-[0.18em] uppercase rounded-full transition-all shadow-sm ring-1 ring-[#7C5A00]/20"
      }
    >
      <Sparkles className="h-3 w-3" />
      Major Events
      {next && (
        <span className="text-[#0A0612]/70 font-normal ml-1 lowercase tracking-normal">
          · next {next}
        </span>
      )}
      <ArrowRight className="h-3 w-3 opacity-70" />
    </Link>
  );
}
